import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { ref } from 'vue';
import { fetchFieldBootstrap, postFieldSync } from '@/mobile/sync/fieldSyncApi';
import {
  clearFieldCache,
  clearOutbox,
  getLastPullAt,
  getOrCreateDeviceId,
  loadFieldCache,
  loadOutbox,
  mergeFieldCacheDelta,
  saveFieldCache,
  type FieldSyncCache,
} from '@/mobile/sync/fieldSyncCache';
import type { FieldSyncResponse } from '@/mobile/sync/fieldSyncTypes';

const syncing = ref(false);
const lastError = ref<string | null>(null);
const cache = ref<FieldSyncCache | null>(null);
const isOnline = ref(true);

let watcherStarted = false;
let networkHandle: { remove: () => void } | undefined;

async function refreshOnlineStatus(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    isOnline.value = navigator.onLine;
    return;
  }

  const status = await Network.getStatus();
  isOnline.value = status.connected;
}

export async function pullFieldBootstrap(): Promise<FieldSyncCache | null> {
  const data = await fetchFieldBootstrap();
  await saveFieldCache(data);
  cache.value = await loadFieldCache();

  return cache.value;
}

export async function runFieldSync(): Promise<FieldSyncResponse | null> {
  if (syncing.value) {
    return null;
  }

  syncing.value = true;
  lastError.value = null;

  try {
    await refreshOnlineStatus();

    if (!isOnline.value) {
      cache.value = await loadFieldCache();
      return null;
    }

    const deviceId = await getOrCreateDeviceId();
    const lastPullAt = await getLastPullAt();
    const outbox = await loadOutbox();

    const response = await postFieldSync({
      deviceId,
      lastPullAt,
      push: outbox.map((item) => item.operation),
    });

    await mergeFieldCacheDelta({
      serverTime: response.serverTime,
      summary: response.pulled.summary,
      projects: response.pulled.projects,
      reports: response.pulled.reports,
      activities: response.pulled.activities,
    });

    if (outbox.length > 0) {
      const hasErrors = response.pushResults.some((r) => r.status === 'error');
      if (!hasErrors) {
        await clearOutbox();
      }
    }

    cache.value = await loadFieldCache();

    return response;
  } catch (error) {
    lastError.value =
      error instanceof Error ? error.message : 'Falha na sincronização.';
    cache.value = await loadFieldCache();

    return null;
  } finally {
    syncing.value = false;
  }
}

export async function initializeFieldSync(): Promise<void> {
  cache.value = await loadFieldCache();
  await refreshOnlineStatus();

  if (!isOnline.value) {
    return;
  }

  const lastPullAt = await getLastPullAt();
  const needsFullLoad =
    !lastPullAt ||
    !cache.value ||
    (cache.value.projects.length === 0 && cache.value.reports.length === 0);

  if (needsFullLoad) {
    await pullFieldBootstrap();
  } else {
    await runFieldSync();
  }
}

export function startFieldSyncWatcher(): void {
  if (watcherStarted) {
    return;
  }

  watcherStarted = true;

  if (!Capacitor.isNativePlatform()) {
    const onOnline = () => {
      isOnline.value = true;
      void runFieldSync();
    };

    window.addEventListener('online', onOnline);
    networkHandle = {
      remove: () => window.removeEventListener('online', onOnline),
    };

    return;
  }

  void Network.addListener('networkStatusChange', (status) => {
    isOnline.value = status.connected;
    if (status.connected) {
      void runFieldSync();
    }
  }).then((handle) => {
    networkHandle = handle;
  });
}

export function stopFieldSyncWatcher(): void {
  networkHandle?.remove();
  networkHandle = undefined;
  watcherStarted = false;
}

export function useFieldSync() {
  return {
    syncing,
    lastError,
    cache,
    isOnline,
    initialize: initializeFieldSync,
    runSync: runFieldSync,
    pullBootstrap: pullFieldBootstrap,
    clearCache: clearFieldCache,
  };
}
